const MAX_CACHE_ENTRIES = 100;
const waveformCache = new Map<string, Promise<number[]>>();

type AudioContextConstructor = new () => AudioContext;

const audioContextConstructor = (): AudioContextConstructor => {
  const constructor =
    window.AudioContext ??
    (window as typeof window & { webkitAudioContext?: AudioContextConstructor })
      .webkitAudioContext;

  if (!constructor) throw new Error("Web Audio is unavailable");
  return constructor;
};

export const samplesFromBuffer = (
  buffer: AudioBuffer,
  barCount: number
): number[] => {
  const channelCount = Math.max(1, buffer.numberOfChannels);
  const sampleCount = buffer.length;
  const samplesPerBar = Math.max(1, Math.floor(sampleCount / barCount));
  const values = Array.from({ length: barCount }, (_, barIndex) => {
    const start = barIndex * samplesPerBar;
    const end = Math.min(sampleCount, start + samplesPerBar);
    let peak = 0;

    for (let channel = 0; channel < channelCount; channel += 1) {
      const samples = buffer.getChannelData(channel);
      for (let index = start; index < end; index += 1) {
        peak = Math.max(peak, Math.abs(samples[index] ?? 0));
      }
    }

    return peak;
  });
  const maximum = Math.max(...values, 0.01);

  return values.map((value) => Math.max(0.12, value / maximum));
};

const decodeWaveform = async (
  source: string,
  barCount: number
): Promise<number[]> => {
  const response = await fetch(source, { credentials: "omit" });
  if (!response.ok) throw new Error("Unable to load audio waveform");

  const Context = audioContextConstructor();
  const context = new Context();
  try {
    const encoded = await response.arrayBuffer();
    const decoded = await context.decodeAudioData(encoded.slice(0));
    return samplesFromBuffer(decoded, barCount);
  } finally {
    await context.close();
  }
};

export const loadWaveform = (
  source: string,
  barCount: number
): Promise<number[]> => {
  const key = `${barCount}:${source}`;
  const cached = waveformCache.get(key);
  if (cached) return cached;

  const pending = decodeWaveform(source, barCount).catch((error) => {
    waveformCache.delete(key);
    throw error;
  });
  waveformCache.set(key, pending);

  if (waveformCache.size > MAX_CACHE_ENTRIES) {
    waveformCache.delete(waveformCache.keys().next().value as string);
  }

  return pending;
};

export const clearWaveformCache = (source?: string) => {
  if (!source) {
    waveformCache.clear();
    return;
  }

  for (const key of waveformCache.keys()) {
    if (key.endsWith(`:${source}`)) waveformCache.delete(key);
  }
};
